import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import axios from "axios";
import { UploadCloud, ClipboardPaste, ShieldCheck, CheckCircle2, XCircle, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Turn a CSV header ("First Name") into a merge-tag key ("first_name"). */
const slugTag = (h) => String(h || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export default function AudienceStep({ data, onChange }) {
  const [mode, setMode] = useState("paste");
  const [csvRows, setCsvRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [emailCol, setEmailCol] = useState("");
  const [mapping, setMapping] = useState({});
  const [paste, setPaste] = useState("");
  const [checkSuppression, setCheckSuppression] = useState(false);
  const [validation, setValidation] = useState(null);
  const [checking, setChecking] = useState(false);

  const recipients = data.recipients || [];

  const onDrop = useCallback((files) => {
    const file = files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const cols = res.meta.fields || [];
        setColumns(cols);
        setCsvRows(res.data);
        const guess = cols.find((c) => /e-?mail/i.test(c)) || cols[0] || "";
        setEmailCol(guess);
        const initMap = {};
        cols.forEach((c) => { if (c !== guess) initMap[c] = slugTag(c); });
        setMapping(initMap);
        buildFromCsv(res.data, guess, cols, initMap);
      },
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { "text/csv": [".csv"] }, multiple: false });

  const buildFromCsv = (rows, col, cols, map) => {
    const seen = new Set();
    const out = [];
    rows.forEach((r) => {
      const email = String(r[col] || "").trim().toLowerCase();
      if (!EMAIL_RE.test(email) || seen.has(email)) return;
      seen.add(email);
      const variables = {};
      cols.forEach((c) => {
        if (c === col) return;
        const tag = (map[c] || "").trim();
        if (!tag) return; // ignored column
        variables[tag] = r[c];
      });
      out.push({ email, variables });
    });
    onChange({ recipients: out });
    setValidation(null);
  };

  const changeEmailCol = (newCol) => {
    setEmailCol(newCol);
    const newMap = {};
    columns.forEach((c) => { if (c !== newCol) newMap[c] = mapping[c] ?? slugTag(c); });
    setMapping(newMap);
    buildFromCsv(csvRows, newCol, columns, newMap);
  };

  const updateMapping = (col, tag) => {
    const newMap = { ...mapping, [col]: tag };
    setMapping(newMap);
    buildFromCsv(csvRows, emailCol, columns, newMap);
  };

  const cleanPaste = () => {
    const seen = new Set();
    const out = [];
    paste.split(/[\n,;]+/).forEach((line) => {
      const email = line.trim().toLowerCase();
      if (!EMAIL_RE.test(email) || seen.has(email)) return;
      seen.add(email); out.push({ email, variables: {} });
    });
    onChange({ recipients: out });
    setValidation(null);
  };

  const runSuppressionCheck = async () => {
    setChecking(true);
    try {
      const { data: v } = await axios.post(`${API}/v1/campaigns/validate`, { emails: recipients.map((r) => r.email) });
      setValidation(v);
    } finally { setChecking(false); }
  };

  const preview = useMemo(() => csvRows.slice(0, 5), [csvRows]);

  return (
    <div data-testid="audience-step">
      <div className="wiz-tabs">
        <button className={`wiz-tab ${mode === "paste" ? "active" : ""}`} data-testid="mode-paste" onClick={() => setMode("paste")}><ClipboardPaste size={14} /> Paste emails</button>
        <button className={`wiz-tab ${mode === "csv" ? "active" : ""}`} data-testid="mode-csv" onClick={() => setMode("csv")}><UploadCloud size={14} /> Upload CSV</button>
      </div>

      {mode === "csv" ? (
        <div className="panel"><div className="p-body">
          <div {...getRootProps()} className={`dropzone ${isDragActive ? "active" : ""}`} data-testid="csv-dropzone">
            <input {...getInputProps()} data-testid="csv-input" />
            <UploadCloud size={30} />
            <p>{isDragActive ? "Drop the CSV here…" : "Drag & drop a CSV, or click to browse"}</p>
            <span className="hint">First row must be headers (one column with email addresses)</span>
          </div>
          {columns.length > 0 && (
            <>
              <div className="field" style={{ marginTop: 16 }}>
                <label>Which column holds the email address?</label>
                <select data-testid="email-column-select" value={emailCol} onChange={(e) => changeEmailCol(e.target.value)}>
                  {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="field" style={{ marginTop: 16 }}>
                <label>Map columns → merge tags</label>
                <p className="hint" style={{ margin: "0 0 8px" }}>Point each CSV column at a <code>{"{{tag}}"}</code> your template uses. Rename freely — no need to touch the CSV. Uncheck to ignore a column.</p>
                <div className="scroll"><table className="tbl" data-testid="column-mapping-table">
                  <thead><tr><th>CSV Column</th><th>Merge tag</th><th style={{ width: 60 }}>Use</th></tr></thead>
                  <tbody>
                    {columns.filter((c) => c !== emailCol).map((c) => {
                      const used = !!(mapping[c] && mapping[c].trim());
                      return (
                        <tr key={c} data-testid={`map-row-${slugTag(c)}`}>
                          <td className="mono-strong">{c}</td>
                          <td>
                            <span style={{ color: "var(--txt-faint)" }}>{"{{"}</span>
                            <input
                              data-testid={`map-input-${slugTag(c)}`}
                              style={{ width: 180, margin: "0 4px", display: "inline-block" }}
                              value={mapping[c] ?? ""}
                              disabled={!used}
                              placeholder="ignored"
                              onChange={(e) => updateMapping(c, e.target.value.replace(/\s+/g, "_"))}
                            />
                            <span style={{ color: "var(--txt-faint)" }}>{"}}"}</span>
                          </td>
                          <td>
                            <input type="checkbox" data-testid={`map-use-${slugTag(c)}`} checked={used}
                              onChange={(e) => updateMapping(c, e.target.checked ? (mapping[c] || slugTag(c)) : "")} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              </div>

              <p className="hint" style={{ marginTop: 10 }}>Preview (first 5 of {csvRows.length} rows):</p>
              <div className="scroll"><table className="tbl"><thead><tr>{columns.map((c) => <th key={c}>{c === emailCol ? `📧 ${c}` : (mapping[c] && mapping[c].trim() ? `${c} → {{${mapping[c]}}}` : `${c} (ignored)`)}</th>)}</tr></thead>
                <tbody data-testid="csv-preview-tbody">{preview.map((r, i) => <tr key={i}>{columns.map((c) => <td key={c}>{String(r[c] ?? "")}</td>)}</tr>)}</tbody></table></div>
            </>
          )}
        </div></div>
      ) : (
        <div className="panel"><div className="p-body">
          <div className="field"><label>Paste email addresses (one per line)</label>
            <textarea data-testid="paste-input" rows={7} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={"ada@example.com\nbob@example.com"} />
          </div>
          <button className="btn ghost" data-testid="clean-validate-btn" onClick={cleanPaste}><CheckCircle2 size={15} /> Clean & validate</button>
        </div></div>
      )}

      <div className="cols" style={{ marginTop: 16 }}>
        <div className="panel"><div className="p-head"><Users size={15} /><h3>Audience</h3></div>
          <div className="p-body">
            <div className="big-stat" data-testid="recipient-count"><span className="num">{recipients.length}</span><span className="lbl">unique valid recipients</span></div>
            <label className="chk" data-testid="suppression-toggle"><input type="checkbox" checked={checkSuppression} onChange={(e) => { setCheckSuppression(e.target.checked); if (e.target.checked && recipients.length) runSuppressionCheck(); }} /> Check against suppression list</label>
            {checkSuppression && (
              <button className="btn ghost" data-testid="run-suppression-btn" disabled={checking || !recipients.length} onClick={runSuppressionCheck}><ShieldCheck size={15} /> {checking ? "Checking…" : "Re-check"}</button>
            )}
          </div>
        </div>
        <div className="panel"><div className="p-head"><ShieldCheck size={15} /><h3>Validation</h3></div>
          <div className="p-body">
            {!validation ? <p className="note">Enable the suppression check to see how many recipients are deliverable.</p> : (
              <div className="val-grid" data-testid="validation-result">
                <div><CheckCircle2 size={14} color="var(--green)" /> Deliverable <b>{validation.deliverable}</b></div>
                <div><ShieldCheck size={14} color="var(--amber)" /> Suppressed <b>{validation.suppressed}</b></div>
                <div><XCircle size={14} color="var(--red)" /> Invalid <b>{validation.invalid}</b></div>
                <div><XCircle size={14} color="var(--txt-faint)" /> Duplicates <b>{validation.duplicates}</b></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
